import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchOne as fetchBrowseHomePage, saveOne as upsertBrowseHomePage } from 'app/api/admin/browse/homePageApi';
import ConfirmUnsavedChangesModal from 'app/components/admin/browse/shared/ConfirmUnsavedChangesModal';
import ErrorPage from 'app/components/ErrorPage';
import { MESSAGE_ACTION_GENERIC_ERROR, MESSAGE_ACTION_GENERIC_SUCCESS } from 'app/constants/admin/browse';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { hasAdminPermissions, makeEmptyBrowseHomePage } from 'app/helpers/admin/browse/home';
import useApiClient from 'app/hooks/useApiClient';
import useLocaleCodes from 'app/hooks/useLocalCodes';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import AdminBrowseHomeEditPage from './AdminBrowseHomeEditPage';

const AdminBrowseHomeEditPageContainer = ({ browseHomePageId, localeCode }) => {
  const apiClient = useApiClient();
  const dispatch = useDispatch();

  const { isAvailableLocaleCode } = useLocaleCodes();

  const currentUser = useSelector(selectCurrentUser);

  const currentLibrary = useSelector(selectCurrentLibrary);
  const subdomain = currentLibrary.get('domain');

  const [state, setState] = useState({
    browseHomePage: null,
    browseHomePageWorkingCopy: null,
    errorMessage: '',
    hasUnsavedChanges: false,
    isFetching: true,
    isSaving: false
  });

  const { browseHomePage, browseHomePageWorkingCopy, errorMessage, hasUnsavedChanges, isFetching, isSaving } = state;

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (browseHomePageId) {
          return fetchBrowseHomePage(apiClient, localeCode, browseHomePageId);
        }

        return Promise.resolve(makeEmptyBrowseHomePage());
      })
      .then(fetchedBrowseHomePage => {
        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: fetchedBrowseHomePage,
            browseHomePageWorkingCopy: cloneDeep(fetchedBrowseHomePage)
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);

        setState(prevState => {
          return {
            ...prevState,
            errorMessage: `Failed to load home page ${browseHomePageId}`
          };
        });
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isFetching: false
          };
        });
      });
  }, [apiClient, browseHomePageId, currentUser, dispatch, localeCode, subdomain]);

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const handleWorkingCopyChange = newBrowseHomePageWorkingCopy => {
    const newHasUnsavedChanges = !isEqual(newBrowseHomePageWorkingCopy, browseHomePage);

    setState(prevState => {
      return {
        ...prevState,
        browseHomePageWorkingCopy: newBrowseHomePageWorkingCopy,
        hasUnsavedChanges: newHasUnsavedChanges
      };
    });
  };

  const saveBrowseHomePage = () => {
    browseHomePageWorkingCopy.updatedAt = Date.now();
    browseHomePageWorkingCopy.updatedBy = currentUser.get('name');

    if (!browseHomePageWorkingCopy.createdBy) {
      browseHomePageWorkingCopy.createdBy = currentUser.get('name');
    }

    setState(prevState => {
      return {
        ...prevState,
        hasUnsavedChanges: false,
        isSaving: true
      };
    });

    upsertBrowseHomePage(apiClient, localeCode, browseHomePageWorkingCopy)
      .then(savedBrowseHomePage => {
        dispatch(MESSAGE_ACTION_GENERIC_SUCCESS);

        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: savedBrowseHomePage,
            browseHomePageWorkingCopy: cloneDeep(savedBrowseHomePage)
          };
        });
      })
      .catch(err => {
        dispatch(MESSAGE_ACTION_GENERIC_ERROR);
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  if (!isAvailableLocaleCode(localeCode)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }
  if (!(errorMessage || isFetching || browseHomePage)) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  return (
    <>
      <ConfirmUnsavedChangesModal hasUnsavedChanges={hasUnsavedChanges} />
      <AdminBrowseHomeEditPage
        browseHomePage={browseHomePageWorkingCopy}
        errorMessage={errorMessage}
        hasUnsavedChanges={hasUnsavedChanges}
        isFetching={isFetching}
        isSaving={isSaving}
        localeCode={localeCode}
        onChange={handleWorkingCopyChange}
        onSave={saveBrowseHomePage}
      />
    </>
  );
};

AdminBrowseHomeEditPageContainer.propTypes = {
  browseHomePageId: PropTypes.string,
  localeCode: PropTypes.oneOf(Object.values(LOCALE_CODE)).isRequired
};

export default AdminBrowseHomeEditPageContainer;
